import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiFetch from '../../../utils/apiFetch';
import { normalizeLayout, snapStickerCenter } from '../../../shared/mapLayout.mjs';

const placeOf = ({ x, y, turn }) => ({ x, y, turn });
const samePlace = (a, b) => a.x === b.x && a.y === b.y && a.turn === b.turn;

/**
 * The DM moving and turning a map's stickers during play. A move shows at once and is sent to the server, one request at a
 * time per sticker: moves made while one is on its way wait, and only the last of them is sent. A refused move goes back
 * and leaves `error` saying why. Everyone, this page included, then gets the committed map from the server.
 */
export default function useMapStickerMoves(campaignId, map) {
  const mapId = map?.mapId;
  // Cleaned as the rules read it, so a map saved before stickers had their name shows them too.
  const raw = map?.layout;
  const layout = useMemo(() => (raw && typeof raw === 'object' && !Array.isArray(raw) ? normalizeLayout(raw) : null), [raw]);
  // Where each sticker is shown until the saved map catches up with it.
  const [shown, setShown] = useState({});
  const [error, setError] = useState(null);
  const sending = useRef({});
  const waiting = useRef({});

  useEffect(() => { setShown({}); setError(null); sending.current = {}; waiting.current = {}; }, [campaignId, mapId]);
  // A sticker no longer on its way is dropped from `shown` once the saved map has it there.
  useEffect(() => {
    setShown((current) => {
      const saved = new Map((layout?.stickers || []).map((sticker) => [sticker.id, sticker]));
      const kept = Object.entries(current).filter(([id, place]) => saved.has(id) && (sending.current[id] || !samePlace(saved.get(id), place)));
      return kept.length === Object.keys(current).length ? current : Object.fromEntries(kept);
    });
  }, [layout]);

  const stickerLayout = useMemo(() => (layout && Object.keys(shown).length
    ? { ...layout, stickers: (layout.stickers || []).map((sticker) => (shown[sticker.id] ? { ...sticker, ...shown[sticker.id] } : sticker)) }
    : layout), [layout, shown]);

  const send = useCallback(async (id) => {
    if (sending.current[id]) return;
    sending.current[id] = true;
    try {
      while (waiting.current[id]) {
        const place = waiting.current[id];
        delete waiting.current[id];
        // eslint-disable-next-line no-await-in-loop
        const response = await apiFetch(`/campaigns/${encodeURIComponent(campaignId)}/maps/${encodeURIComponent(mapId)}/stickers/${encodeURIComponent(id)}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(place),
        });
        // eslint-disable-next-line no-await-in-loop
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.message || 'The sticker could not be moved.');
        if (!waiting.current[id] && body.sticker) setShown((current) => ({ ...current, [id]: placeOf(body.sticker) }));
      }
    } catch (failure) {
      delete waiting.current[id];
      setShown((current) => { const { [id]: _dropped, ...rest } = current; return rest; });
      setError(failure.message);
    } finally {
      sending.current[id] = false;
    }
  }, [campaignId, mapId]);

  const move = useCallback((id, place) => {
    const sticker = (layout?.stickers || []).find((entry) => entry.id === id);
    if (!sticker || !campaignId || !mapId) return;
    const turn = ((Math.round(place.turn) % 360) + 360) % 360;
    const next = { ...snapStickerCenter(layout, sticker.kind, place, turn), turn };
    setError(null);
    setShown((current) => ({ ...current, [id]: next }));
    waiting.current[id] = next;
    send(id);
  }, [layout, campaignId, mapId, send]);

  return { layout: stickerLayout, move, error };
}
